import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useAuth, useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can, permissionUnion } from '../../lib/permissions';
import { byModule, KEYS, moduleName, type Permission, type Role, usePermissions, useRoles } from './data';

type Selection = { kind: 'ninguno' } | { kind: 'nuevo' } | { kind: 'rol'; id: string };

/** Roles y sus permisos: listar, crear, editar y borrar (guía 5.2 y 5.3). */
export function RolesPage() {
  const me = useMe();
  const roles = useRoles();
  const permissions = usePermissions();
  const [selection, setSelection] = useState<Selection>({ kind: 'ninguno' });
  const [notice, setNotice] = useState<string | null>(null);
  const canManage = can(me, 'plataforma.roles.gestionar');

  const error = roles.error ?? permissions.error;
  const selected = selection.kind === 'rol' ? roles.data?.find((r) => r.id === selection.id) : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('acceso.roles_titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('acceso.roles_descripcion')}</p>
        </div>
        {canManage && (
          <Button
            onClick={() => {
              setNotice(null);
              setSelection({ kind: 'nuevo' });
            }}
          >
            {t('acceso.nuevo_rol')}
          </Button>
        )}
      </div>
      {error && <Alert>{errorMessage(error)}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {!roles.data || !permissions.data ? (
        !error && <Spinner label={t('comun.cargando')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
          <Card className="self-start p-2">
            <ul aria-label={t('acceso.roles_titulo')}>
              {roles.data.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    aria-current={selection.kind === 'rol' && selection.id === r.id ? 'true' : undefined}
                    className={`w-full rounded px-3 py-2 text-left hover:bg-slate-50 ${selection.kind === 'rol' && selection.id === r.id ? 'bg-slate-100' : ''}`}
                    onClick={() => {
                      setNotice(null);
                      setSelection({ kind: 'rol', id: r.id });
                    }}
                  >
                    <span className="block text-sm font-medium text-slate-900">{r.name}</span>
                    <span className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                      {r.grantsAll ? t('acceso.permisos_todos') : t('acceso.permisos_contador', { n: r.permissions.length })}
                      {r.isSystem && <Badge>{t('acceso.rol_inicial')}</Badge>}
                      {r.requiresMfa && <Badge tone="blue">{t('acceso.rol_2fa_corto')}</Badge>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {selection.kind === 'ninguno' || (selection.kind === 'rol' && !selected) ? (
            <p className="text-sm text-slate-600">{t('acceso.elegir_rol')}</p>
          ) : (
            <RoleEditor
              key={selection.kind === 'rol' ? selection.id : 'nuevo'}
              role={selected ?? null}
              catalog={permissions.data}
              canManage={canManage}
              onDone={(message, next) => {
                setNotice(message);
                setSelection(next);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Sugiere la clave a partir del nombre: "Técnico Redes" → "tecnico_redes". */
function suggestKey(name: string): string {
  const key = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 41);
  return /^[a-z]/.test(key) || key === '' ? key : `rol_${key}`.slice(0, 41);
}

function RoleEditor({
  role,
  catalog,
  canManage,
  onDone,
}: {
  /** null = rol nuevo. */
  role: Role | null;
  catalog: Permission[];
  canManage: boolean;
  onDone: (message: string, next: Selection) => void;
}) {
  const me = useMe();
  const { reloadMe } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(role?.name ?? '');
  const [key, setKey] = useState(role?.key ?? '');
  const [keyEdited, setKeyEdited] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(role?.requiresMfa ?? false);
  const [selected, setSelected] = useState(() => new Set(role?.permissions ?? []));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const readOnly = !canManage || (role?.grantsAll ?? false);
  const mine = permissionUnion(me);
  const original = new Set(role?.permissions ?? []);
  // Solo se puede agregar lo que uno tiene; lo que el rol ya tenía se puede quitar y volver a marcar.
  const grantable = (k: string) => mine.has(k) || original.has(k);

  function toggle(keys: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (on && grantable(k)) next.add(k);
        else if (!on) next.delete(k);
      }
      return next;
    });
  }

  /** Ejecuta el cambio, recarga los roles y recién entonces muestra el resultado. */
  async function act(fn: () => Promise<[string, Selection]>) {
    setBusy(true);
    setError(null);
    try {
      const [message, next] = await fn();
      await qc.invalidateQueries({ queryKey: KEYS.roles });
      // Si el actor tiene este rol, sus permisos efectivos pudieron cambiar.
      await reloadMe();
      onDone(message, next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const save = (e: FormEvent) => {
    e.preventDefault();
    const permissions = [...selected].sort();
    return act(async () => {
      if (!role) {
        const created = await api<{ id: string }>('/api/acceso/roles', { json: { key, name: name.trim(), requiresMfa, permissions } });
        return [t('acceso.rol_creado'), { kind: 'rol', id: created.id }];
      }
      await api(`/api/acceso/roles/${role.id}`, {
        method: 'PATCH',
        json: { name: name.trim(), permissions, ...(role.isSystem ? {} : { requiresMfa }) },
      });
      return [t('acceso.rol_guardado'), { kind: 'rol', id: role.id }];
    });
  };

  const remove = () =>
    act(async () => {
      await api(`/api/acceso/roles/${role!.id}`, { method: 'DELETE' });
      return [t('acceso.rol_borrado'), { kind: 'ninguno' }];
    });

  return (
    <Card title={role ? role.name : t('acceso.nuevo_rol')}>
      <form onSubmit={(e) => void save(e)} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {!canManage && <Alert tone="info">{t('acceso.solo_lectura')}</Alert>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="rol-nombre"
            label={t('acceso.rol_nombre')}
            value={name}
            maxLength={80}
            required
            disabled={readOnly}
            onChange={(e) => {
              setName(e.target.value);
              if (!role && !keyEdited) setKey(suggestKey(e.target.value));
            }}
          />
          <div className="space-y-1">
            <Field
              id="rol-clave"
              label={t('acceso.rol_clave')}
              value={key}
              required
              pattern="[a-z][a-z0-9_]{2,40}"
              disabled={!!role || readOnly}
              onChange={(e) => {
                setKeyEdited(true);
                setKey(e.target.value);
              }}
            />
            {!role && <p className="text-xs text-slate-500">{t('acceso.rol_clave_ayuda')}</p>}
          </div>
        </div>

        <div className="space-y-1">
          <Checkbox
            label={t('acceso.rol_2fa')}
            checked={requiresMfa}
            disabled={readOnly || role?.isSystem}
            onChange={(e) => setRequiresMfa(e.target.checked)}
          />
          {role?.isSystem && !role.grantsAll && <p className="pl-6 text-xs text-slate-500">{t('acceso.rol_2fa_fijo')}</p>}
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('acceso.permisos')}</legend>
          {role?.grantsAll ? (
            <Alert tone="info">{t('acceso.rol_todos')}</Alert>
          ) : (
            byModule(catalog).map(({ module, items }) => {
              const keys = items.map((p) => p.key);
              const allOn = keys.every((k) => selected.has(k) || !grantable(k)) && keys.some((k) => selected.has(k));
              return (
                <div key={module} className="rounded-md border border-slate-200">
                  <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="text-sm font-medium">{moduleName(module)}</span>
                    {!readOnly && (
                      <Checkbox
                        label={t('acceso.permisos_todos')}
                        aria-label={`${t('acceso.permisos_todos')}: ${moduleName(module)}`}
                        checked={allOn}
                        onChange={(e) => toggle(keys, e.target.checked)}
                      />
                    )}
                  </div>
                  <div className="grid gap-2 p-3 md:grid-cols-2">
                    {items.map((p) => (
                      <Checkbox
                        key={p.key}
                        checked={selected.has(p.key)}
                        disabled={readOnly || (!selected.has(p.key) && !grantable(p.key))}
                        hint={grantable(p.key) ? undefined : t('acceso.permiso_ajeno')}
                        onChange={(e) => toggle([p.key], e.target.checked)}
                        label={
                          <>
                            {p.description}
                            <span className="block font-mono text-xs text-slate-400">{p.key}</span>
                          </>
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </fieldset>

        {!readOnly && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="submit" disabled={busy || !name.trim() || !key}>
              {t('comun.guardar')}
            </Button>
            {role && !role.isSystem && !confirmDelete && (
              <Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirmDelete(true)}>
                {t('acceso.borrar_rol')}
              </Button>
            )}
          </div>
        )}
        {confirmDelete && role && (
          <Alert tone="warning">
            <p>{t('acceso.borrar_rol_confirmar', { rol: role.name })}</p>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="danger" disabled={busy} onClick={() => void remove()}>
                {t('acceso.si_borrar')}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setConfirmDelete(false)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        )}
      </form>
    </Card>
  );
}
